'use client';
import { motion, useReducedMotion, type HTMLMotionProps } from 'framer-motion';

const EASE = [0.16, 1, 0.3, 1] as const;
const VIEW = { once: true, margin: '0px 0px -12% 0px' } as const;

type Tag = 'h1' | 'h2' | 'h3' | 'p' | 'span';

/** Titre qui monte mot par mot, chaque mot sortant d'un masque. */
export function Words({ text, as = 'h2', className = '', delay = 0 }: { text: string; as?: Tag; className?: string; delay?: number }) {
    const reduce = useReducedMotion();
    const Tag = motion[as];
    return (
        <Tag className={className} initial="hidden" whileInView="shown" viewport={VIEW}>
            <span className="sr-only">{text}</span>
            {text.split(' ').map((w, i) => (
                <span key={i} aria-hidden className="inline-block overflow-hidden pb-[0.08em] align-top">
                    <motion.span
                        className="inline-block will-change-transform"
                        variants={{ hidden: reduce ? {} : { y: '105%', rotate: 4 }, shown: { y: '0%', rotate: 0 } }}
                        transition={{ duration: 0.9, delay: delay + i * 0.06, ease: EASE }}
                    >
                        {w}
                        {' '}
                    </motion.span>
                </span>
            ))}
        </Tag>
    );
}

/** Bloc qui apparaît en fondu, léger flou dissipé. */
export function Fade({ delay = 0, className, children, ...rest }: HTMLMotionProps<'div'> & { delay?: number }) {
    const reduce = useReducedMotion();
    return (
        <motion.div
            className={className}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24, filter: 'blur(8px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={VIEW}
            transition={{ duration: 0.9, delay, ease: EASE }}
            {...rest}
        >
            {children}
        </motion.div>
    );
}

/** Lettres qui ondulent au survol du parent `.group` (styles .pf-wave dans portfolio.css). */
export function Wave({ text }: { text: string }) {
    return (
        <span className="pf-wave">
            <span className="sr-only">{text}</span>
            {[...text].map((c, i) => (
                <span key={i} aria-hidden style={{ '--i': i } as React.CSSProperties}>
                    {c === ' ' ? '\u00a0' : c}
                </span>
            ))}
        </span>
    );
}
